import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowBackRounded } from '@mui/icons-material';
import Busy from '../../components/Busy';
import { useCreateLocationWizard } from './useCreateLocationWizard';
import { invalidateLocationManagers } from '../../hooks/location/useLocationManagers';
import { resolveCancelIntent } from './locationUpdateHelpers';
import { isManagerFormBlockingFooter } from './newLocationManager';
import WizardStepIndicator from './WizardStepIndicator';
import WizardFooter from './WizardFooter';
import { WizardFooterAction } from './wizardFooterConfig';
import LocationInformationStep from './steps/LocationInformationStep';
import LocationSettingsStep from './steps/LocationSettingsStep';
import LocationManagerStep from './steps/LocationManagerStep';
import LocationFeaturesStep from './steps/LocationFeaturesStep';
import LocationPricingStep from './steps/LocationPricingStep';
import LocationAdminSettingsStep from './steps/LocationAdminSettingsStep';
import { EnrichmentVersionProvider } from './EnrichmentVersionContext';

interface WizardStepPanelProps {
  active: boolean;
  index: number;
  children: React.ReactNode;
}

/**
 * Wrapper that keeps a step mounted while hiding it when inactive. Using the
 * `hidden` attribute (rather than conditionally rendering) means each step's
 * local state and any SDK objects (Google Maps autocomplete/map, cached manager
 * list) persist across navigation, so returning to a step is instant with no
 * re-fetch or re-initialization. `display: none` also removes inactive panels
 * from tab order automatically.
 */
const WizardStepPanel = (props: WizardStepPanelProps) => {
  const { active, index, children } = props;
  return (
    <Box hidden={!active} id={`wizard-step-panel-${index}`}>
      {children}
    </Box>
  );
};

/**
 * Multi-step wizard for creating or editing a supplier location profile.
 *
 * Suppliers see five steps (Info → Location Settings → Manager → Features →
 * Pricing); SecurSpace admins additionally see a sixth admin-only step
 * (Admin Settings). Every step is clickable in the indicator and auto-saves the
 * current step before jumping. Intermediate saves never change `live`, so
 * editing a live location preserves its published status.
 *
 * All steps stay mounted at once (inactive ones hidden) so navigating back and
 * forth is instant and preserves each step's state and any loaded SDK/data.
 */
const CreateLocationWizard = () => {
  // A prior wizard session may have cached this account's manager list at the
  // module level. Clear it once on open — in a layout effect (not during render)
  // so rendering stays side-effect free — before any wizard step mounts. The
  // steps are gated on `managersInvalidated` below so the Manager step never
  // mounts (and never fetches) against a stale cache; once cleared, its fetch
  // reflects any Manage Users edits made since the list was last loaded.
  const [managersInvalidated, setManagersInvalidated] = useState(false);
  useLayoutEffect(() => {
    invalidateLocationManagers();
    setManagersInvalidated(true);
  }, []);

  const onError = useCallback((message: string) => toast.error(message), []);
  // The location and manager assignment still saved; only text notifications could not be
  // enabled. Warn rather than error so the styling matches the non-blocking outcome.
  const onWarning = useCallback((message: string) => toast.warn(message), []);
  const onSuccess = useCallback((message: string) => toast.success(message), []);

  // Bumped once per successful enrichment application (see `LocationInformationStep`).
  // Provided via `EnrichmentVersionContext` so any field/step that needs to
  // remount on autofill (Location Name, Location Description, Minimum Number
  // of Spaces, Location Capacity, Phone Number) can read it directly — via
  // `AutofillTextField` or `useEnrichmentVersion()` — without it being
  // threaded through every intermediate component's props.
  const [enrichmentVersion, setEnrichmentVersion] = useState(0);
  const bumpEnrichmentVersion = useCallback(() => setEnrichmentVersion((v) => v + 1), []);

  const {
    isEdit,
    isAdmin,
    loading,
    saving,
    data,
    currentStep,
    steps,
    completedSteps,
    stepErrors,
    listingFile,
    galleryFiles,
    galleryReplacements,
    footerConfig,
    updateData,
    applyEnrichmentData,
    setListingFile,
    setGalleryFiles,
    replaceGalleryImage,
    saveDraftAndAdvance,
    save,
    submitForReview,
    goToStep,
    goBack,
    isDirty,
    checkingAddress,
    addressConfirmationDialog,
    handleAddressConfirm,
    handleAddressConfirmClose,
    saveAndExit,
    discardAndExit,
  } = useCreateLocationWizard(onError, onWarning, onSuccess);

  // Whether the "You have unsaved changes" prompt (opened by Close while the
  // current step is dirty) is showing.
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);

  // Close: prompt to save or discard when the current step has unsynced edits;
  // otherwise just leave.
  const handleCancel = useCallback(() => {
    if (resolveCancelIntent(isDirty) === 'prompt') {
      setShowCancelPrompt(true);
    } else {
      discardAndExit();
    }
  }, [isDirty, discardAndExit]);

  const handleDiscardChanges = useCallback(() => {
    setShowCancelPrompt(false);
    discardAndExit();
  }, [discardAndExit]);

  const handleSaveChanges = useCallback(() => {
    setShowCancelPrompt(false);
    void saveAndExit();
  }, [saveAndExit]);

  // Map the role/status-driven footer action to its wizard handler. Typing this
  // as an exhaustive Record over WizardFooterAction forces a compile error if a
  // new footer action is added without a corresponding handler, preventing the
  // lookup below from silently resolving to undefined at runtime.
  const footerActionHandlers: Record<WizardFooterAction, () => Promise<void>> = {
    next: saveDraftAndAdvance,
    finish: saveDraftAndAdvance,
    save,
    submitForReview,
  };
  // Next and Save both would create a user from the "+ Add New Manager" form, so neither is
  // available until its fields are filled in and valid.
  const newManagerIncomplete = isManagerFormBlockingFooter(currentStep, data.newLocationManager);

  const onPrimary = footerActionHandlers[footerConfig.primary.action];
  const onSecondary = footerActionHandlers[footerConfig.secondary.action];

  // Drive the global loading overlay while the existing location loads (edit mode).
  useEffect(() => {
    Busy.set(loading);
    return () => Busy.set(false);
  }, [loading]);

  // Scroll back to the top whenever the step changes so the supplier starts at
  // the section title rather than wherever they left off on the previous step.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Hold rendering of the wizard steps until the manager cache has been cleared
  // (above) so the Manager step mounts against a clean cache, and while the
  // existing location loads in edit mode.
  if (loading || !managersInvalidated) {
    return null;
  }

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false}>
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', maxWidth: 1040, mx: 'auto'}}>
          <Box sx={{ px: { xs: 2, sm: 4 }, pt: 3, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
            {/* Same path as the footer's Close, so unsaved edits still prompt. */}
            <Button
              variant="text"
              color="inherit"
              size="small"
              startIcon={<ArrowBackRounded fontSize="small" />}
              onClick={handleCancel}
              disabled={saving}
              sx={{
                ml: -1,
                mb: 1,
                color: '#5C6771',
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: 0,
                textTransform: 'uppercase',
              }}
            >
              Manage Locations
            </Button>
            <Typography variant="h5" sx={{ mb: 3 }}>
              {isEdit ? 'Edit Location Profile' : 'Create Location Profile'}
            </Typography>
            <WizardStepIndicator
              steps={steps}
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={goToStep}
              disabled={saving}
            />
          </Box>

          <EnrichmentVersionProvider value={enrichmentVersion}>
            <Box sx={{ px: { xs: 2, sm: 4 }, py: 4 }}>
              <WizardStepPanel active={currentStep === 0} index={0}>
                <LocationInformationStep
                  data={data}
                  updateData={updateData}
                  applyEnrichmentData={applyEnrichmentData}
                  errors={stepErrors}
                  listingFile={listingFile}
                  setListingFile={setListingFile}
                  galleryFiles={galleryFiles}
                  setGalleryFiles={setGalleryFiles}
                  galleryReplacements={galleryReplacements}
                  replaceGalleryImage={replaceGalleryImage}
                  onEnrichmentApplied={bumpEnrichmentVersion}
                  checkingAddress={checkingAddress}
                  addressConfirmationDialog={addressConfirmationDialog}
                  onAddressConfirm={handleAddressConfirm}
                  onAddressConfirmClose={handleAddressConfirmClose}
                />
              </WizardStepPanel>

              <WizardStepPanel active={currentStep === 1} index={1}>
                <LocationSettingsStep data={data} updateData={updateData} errors={stepErrors} />
              </WizardStepPanel>

              <WizardStepPanel active={currentStep === 2} index={2}>
                <LocationManagerStep data={data} updateData={updateData} />
              </WizardStepPanel>

              <WizardStepPanel active={currentStep === 3} index={3}>
                <LocationFeaturesStep data={data} updateData={updateData} />
              </WizardStepPanel>

              <WizardStepPanel active={currentStep === 4} index={4}>
                <LocationPricingStep data={data} updateData={updateData} errors={stepErrors} />
              </WizardStepPanel>

              {/* Step 6 (Admin Settings) is only injected into `steps` for admins. */}
              {isAdmin && (
                <WizardStepPanel active={currentStep === 5} index={5}>
                  <LocationAdminSettingsStep data={data} updateData={updateData} />
                </WizardStepPanel>
              )}
            </Box>
          </EnrichmentVersionProvider>


          <WizardFooter
            backDisabled={currentStep === 0 || saving}
            nextDisabled={saving || newManagerIncomplete}
            primaryLabel={footerConfig.primary.label}
            secondaryLabel={footerConfig.secondary.label}
            secondaryDisabled={saving || !isDirty || newManagerIncomplete}
            onCancel={handleCancel}
            onBack={goBack}
            onPrimary={onPrimary}
            onSecondary={onSecondary}
          />
        </Paper>
      </Container>

      {/* Close with unsaved edits: let the supplier save or discard the current
          step's changes before leaving. */}
      <Dialog
        open={showCancelPrompt}
        onClose={() => setShowCancelPrompt(false)}
        aria-labelledby="wizard-cancel-title"
        aria-describedby="wizard-cancel-description"
      >
        <DialogTitle id="wizard-cancel-title">Unsaved changes</DialogTitle>
        <DialogContent>
          <DialogContentText id="wizard-cancel-description">
            You have unsaved changes on this page. Would you like to save them before leaving?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setShowCancelPrompt(false)} disabled={saving}>
            Keep Editing
          </Button>
          <Button color="error" onClick={handleDiscardChanges} disabled={saving}>
            Discard Changes
          </Button>
          <Button variant="contained" color="primary" onClick={handleSaveChanges} disabled={saving}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateLocationWizard;

